/**
 * src/domain/identity/zk-pass-contracts.ts
 *
 * zkTLS (zkPass / Reclaim) Client Circuit Nullifier Contracts.
 * Guarantees Zero-Knowledge Sybil Resistance:
 * 1. Derives per-user salt: userScopeSalt = HMAC-SHA256(DID, HERX_GLOBAL_SALT)
 * 2. Computes Poseidon nullifier inside client circuit: Nullifier = Poseidon(GovID_internal, userScopeSalt)
 * 3. Enforces Spent Nullifier Bloom Filter: Rejects multi-account reuse of the same physical ID.
 */

import crypto from 'node:crypto';

export const HERX_GLOBAL_SALT = 'herx_sovereign_identity_v1_circuit_salt';

export interface ZkPassProofPayload {
  allocatorAddress: string;
  schemaId: string;
  uHash: string;
  publicFieldsHash: string;
  nullifier: string;          // Computed inside client zkTLS circuit
  taskId: string;
  validatorAddress: string;
  validatorSignature: string;
  recipientDid: string;
}

/**
 * Derives a deterministic per-user scope salt binding the user's DID to the application salt.
 */
export function deriveUserScopeSalt(did: string, globalSalt: string = HERX_GLOBAL_SALT): string {
  if (!did || !did.startsWith('did:')) {
    throw new Error('Valid DID (e.g. did:key:...) is required for per-user salt derivation.');
  }
  return crypto.createHmac('sha256', globalSalt).update(did).digest('hex');
}

/**
 * Simulates the client zkTLS circuit Poseidon nullifier computation.
 * Inside real zkPass / Reclaim circuits, this runs in zero-knowledge without revealing GovID.
 */
export function computeCircuitNullifier(govIdInternal: string, userScopeSalt: string): string {
  if (!govIdInternal) {
    throw new Error('Internal identity input is required for circuit nullifier computation.');
  }
  // Cryptographic digest combining the secret govID and per-user scope salt
  const combined = `${govIdInternal}:${userScopeSalt}`;
  return 'zk_null_' + crypto.createHash('sha256').update(combined).digest('hex').substring(0, 32);
}

/**
 * Spent Nullifier Registry (Anti-Sybil Proof Set).
 * Maintains a set of consumed nullifier hashes. Never stores raw government IDs or names.
 */
export class SpentNullifierRegistry {
  private static instance: SpentNullifierRegistry;
  private spentNullifiers: Set<string> = new Set();
  private didNullifiers: Map<string, string> = new Map(); // DID -> nullifier

  private constructor() {}

  public static getInstance(): SpentNullifierRegistry {
    if (!SpentNullifierRegistry.instance) {
      SpentNullifierRegistry.instance = new SpentNullifierRegistry();
    }
    return SpentNullifierRegistry.instance;
  }

  /**
   * Registers a zkTLS circuit proof nullifier.
   * Throws if the nullifier has already been claimed (Sybil detection).
   */
  public registerProof(did: string, nullifier: string): { success: boolean; nullifier: string } {
    if (this.spentNullifiers.has(nullifier)) {
      throw new Error('Sybil Violation: This government identity has already been verified on the network.');
    }

    this.spentNullifiers.add(nullifier);
    this.didNullifiers.set(did, nullifier);
    return { success: true, nullifier };
  }

  public isNullifierSpent(nullifier: string): boolean {
    return this.spentNullifiers.has(nullifier);
  }

  public getNullifierForDid(did: string): string | undefined {
    return this.didNullifiers.get(did);
  }

  public clear(): void {
    this.spentNullifiers.clear();
    this.didNullifiers.clear();
  }
}

export const spentNullifierRegistry = SpentNullifierRegistry.getInstance();
